/**
 * DISTRIBUTOR-ORDERS MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other product modules directly.
 */

import { supabase } from "../../../shared/utils/supabaseClient.js";
import { getMyPrimaryAccount } from "../../../shared/utils/accountService.js";
import { fetchAllRows } from "../../../shared/utils/fetchAllRows.js";
import { monthRange, monthStartFromNameYear } from "./monthDate.js";

// =====================================================================
// ordersService :: Distributor Orders data over RLS (Supabase Auth)
//
// Ported from the source app, where the Distributor
// Orders screen lived inside the forecasting module and every call went
// through a service-role `forecasting` edge function (Azure-AD model).
// Brand Command uses Supabase Auth + RLS, so — like forecastService /
// mapsService — we talk to the tables DIRECTLY with the authenticated
// client. RLS (migration 0049) pins every row to the caller's account via
// is_account_member(account_id).
//
// Writes use an explicit account_id (the caller's primary workspace) so the
// RLS WITH CHECK passes; DB-controlled columns (id, sort_order, timestamps)
// are never sent from the client.
//
// CROSS-MODULE NOTE: loadForecastRowsForMonths reads the `forecasts` table,
// which is OWNED by the sales-forecasting module. This is the Forecast-vs-
// Orders comparison tab's single documented cross-module data dependency —
// see ARCHITECTURE.md. It is a read-only, RLS-scoped query, not an import.
//
// DEFERRED (not ported — backends don't exist on Brand Command yet): QBO
// invoice posting, production-hub depletion, order-shipped / invoice-posted
// e-mails.
// =====================================================================

const PO_BUCKET = "distributor-order-pos";

// ── Active account + caller identity (cached for the session) ───────────────
let _accountIdPromise = null;
let _emailPromise = null;

export function resetActiveAccount() {
  _accountIdPromise = null;
  _emailPromise = null;
}

// ⚠️ Resolve the tenant through the SHARED getMyPrimaryAccount() and nothing
// else. This used to re-implement the pick locally (owner → admin → first
// joined) straight off `account_members`, which silently ignored the workspace
// switcher's stored choice: a user in two workspaces who switched to the second
// one still got tab content built from the FIRST one's products — one tenant's
// SKUs rendered inside another tenant. Every other service already routes
// through the shared resolver; a second copy of the rule is how they drift.
async function activeAccountId() {
  if (_accountIdPromise) return _accountIdPromise;
  _accountIdPromise = (async () => {
    const acct = await getMyPrimaryAccount();
    if (!acct?.accountId) throw new Error("No workspace found for your account.");
    return acct.accountId;
  })();
  return _accountIdPromise;
}

// Caller e-mail, used for audit columns (updated_by / uploaded_by). Derived
// from the session so component call sites don't have to thread it.
async function callerEmail() {
  if (_emailPromise) return _emailPromise;
  _emailPromise = (async () => {
    const { data: { user } } = await supabase.auth.getUser();
    return user?.email ?? null;
  })();
  return _emailPromise;
}

// =====================================================================
// REFERENCE DATA  (sales_states + products + dimensions)
// =====================================================================

/**
 * States with their region + distributor, for the Orders grid's State /
 * Distributor selectors and filter bar. Shape matches what OrderFlow expects:
 *   [{ state_name, region, distributor }]
 */
export async function loadStatesWithRegions() {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("sales_states")
    .select("state_name, region, distributor, active")
    .eq("account_id", acct)
    .order("state_name", { ascending: true });
  if (error) {
    console.error("[ordersService.loadStatesWithRegions]", error);
    throw new Error("Could not load states.");
  }
  return (data ?? []).filter((r) => r.active !== false);
}

/**
 * Support bundle for the Summary + Forecast-Comparison tabs:
 *   { regions, stateNames, stateRegionMap, stateDistributorMap,
 *     stateDistributorsMap, distributors, sizes, packs, flavors }
 */
export async function loadComparisonSupport() {
  const acct = await activeAccountId();
  const [statesRes, sizesRes, packsRes, flavorsRes] = await Promise.all([
    supabase.from("sales_states")
      .select("state_name, region, distributor, active").eq("account_id", acct),
    // Sizes/packs keep the account's own ordering — the size subtotal bands on
    // the Summary + Comparison tabs are built from these, in this order.
    supabase.from("product_sizes").select("name, sort_order").eq("account_id", acct)
      .order("sort_order", { ascending: true }),
    supabase.from("pack_sizes").select("name, sort_order").eq("account_id", acct)
      .order("sort_order", { ascending: true }),
    supabase.from("product_flavors").select("name").eq("account_id", acct),
  ]);
  for (const [k, r] of Object.entries({ statesRes, sizesRes, packsRes, flavorsRes })) {
    if (r.error) { console.error(`[ordersService.loadComparisonSupport:${k}]`, r.error); throw new Error("Could not load reference data."); }
  }

  const states = (statesRes.data ?? []).filter((s) => s.active !== false);
  const regions = [...new Set(states.map((s) => s.region).filter(Boolean))].sort();
  const stateNames = [...new Set(states.map((s) => s.state_name).filter(Boolean))].sort();
  const stateRegionMap = {};
  const stateDistributorMap = {};
  const stateDistributorsMap = {};
  for (const s of states) {
    if (s.state_name && s.region) stateRegionMap[s.state_name] = s.region;
    if (s.state_name && s.distributor) {
      if (!stateDistributorMap[s.state_name]) stateDistributorMap[s.state_name] = s.distributor;
      (stateDistributorsMap[s.state_name] ||= new Set()).add(s.distributor);
    }
  }
  for (const k of Object.keys(stateDistributorsMap)) stateDistributorsMap[k] = [...stateDistributorsMap[k]].sort();

  // Distributors come from sales_states — the same source of truth every other
  // module uses, and the only one System Settings actually writes. This used to
  // read the standalone `distributors` table, which has no write path anywhere
  // in the app, so the Forecast-Comparison distributor filter was empty for
  // every real tenant while the rest of the app's filters worked.
  const distributors = [
    ...new Set(states.map((s) => s.distributor).filter(Boolean)),
  ].sort();
  // NOT sorted alphabetically — the account's own sort_order is the order the
  // size bands and package groups appear in.
  const sizes = [...new Set((sizesRes.data ?? []).map((r) => r.name).filter(Boolean))];
  const packs = [...new Set((packsRes.data ?? []).map((r) => r.name).filter(Boolean))];
  const flavors = [...new Set((flavorsRes.data ?? []).map((r) => r.name).filter(Boolean))].sort();

  return { regions, stateNames, stateRegionMap, stateDistributorMap, stateDistributorsMap, distributors, sizes, packs, flavors };
}

/**
 * Products with size/pack/flavor names flattened, for the comparison's SKU
 * list + dimension filters. Shape: [{ name, size, pack, flavor, active }].
 */
export async function loadProductsWithDetails() {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("products")
    .select("name, active, size:product_sizes(name), pack:pack_sizes(name), flavor:product_flavors(name)")
    .eq("account_id", acct)
    .order("name", { ascending: true });
  if (error) {
    console.error("[ordersService.loadProductsWithDetails]", error);
    throw new Error("Could not load products.");
  }
  return (data ?? []).map((p) => ({
    name: p.name,
    active: p.active,
    size: p.size?.name ?? "",
    pack: p.pack?.name ?? "",
    flavor: p.flavor?.name ?? "",
  }));
}

// =====================================================================
// SKU IDENTITY
//
// order_items.product_id is the SKU's identity; sku_name is a denormalised
// label. Resolving the label through the FK on read is what makes renaming a
// SKU in System Settings safe — before the FK existed, a rename orphaned every
// historical line because the grid matched on the string.
// =====================================================================

/** { byId: Map<uuid,name>, byName: Map<name,uuid> } for the caller's account. */
async function productIndex() {
  const acct = await activeAccountId();
  const rows = await fetchAllRows(
    () => supabase.from("products").select("id, name").eq("account_id", acct)
      .order("name", { ascending: true }),
    { label: "products (index)" }
  );
  const byId = new Map();
  const byName = new Map();
  for (const r of rows) {
    if (!r?.id || !r?.name) continue;
    byId.set(r.id, r.name);
    if (!byName.has(r.name)) byName.set(r.name, r.id);
  }
  return { byId, byName };
}

/** Current SKU label for a line, preferring the FK over the stored string. */
function resolveSkuName(item, byId) {
  return (item?.product_id && byId.get(item.product_id)) || item?.sku_name || "";
}

// =====================================================================
// ORDERS GRID
// =====================================================================

/** Everything the Orders grid needs: { orders, items, skus, sizes, packs }. */
export async function loadOrderFlowData() {
  const acct = await activeAccountId();
  // order_items is orders x SKUs — past the 1000-row cap after ~60 orders.
  const [ordersRes, itemsRes, productsRes, sizesRes, packsRes] = await Promise.allSettled([
    fetchAllRows(() => supabase.from("orders").select("*").eq("account_id", acct)
      .order("sort_order", { ascending: true }), { label: "orders" }),
    fetchAllRows(() => supabase.from("order_items").select("*").eq("account_id", acct)
      .order("sort_order", { ascending: true }).order("id", { ascending: true }), { label: "order_items" }),
    fetchAllRows(() => supabase.from("products").select("id, name, active").eq("account_id", acct)
      .order("name", { ascending: true }), { label: "products" }),
    fetchAllRows(() => supabase.from("product_sizes").select("name, sort_order").eq("account_id", acct)
      .order("sort_order", { ascending: true }), { label: "product_sizes" }),
    fetchAllRows(() => supabase.from("pack_sizes").select("name, sort_order").eq("account_id", acct)
      .order("sort_order", { ascending: true }), { label: "pack_sizes" }),
  ]);
  if (ordersRes.status   === "rejected") { console.error("[ordersService.loadOrderFlowData:orders]", ordersRes.reason);   throw new Error("Could not load orders."); }
  if (itemsRes.status    === "rejected") { console.error("[ordersService.loadOrderFlowData:items]", itemsRes.reason);     throw new Error("Could not load order items."); }
  if (productsRes.status === "rejected") { console.error("[ordersService.loadOrderFlowData:products]", productsRes.reason); throw new Error("Could not load products."); }
  if (sizesRes.status    === "rejected") { console.error("[ordersService.loadOrderFlowData:sizes]", sizesRes.reason);     throw new Error("Could not load bottle sizes."); }
  if (packsRes.status    === "rejected") { console.error("[ordersService.loadOrderFlowData:packs]", packsRes.reason);     throw new Error("Could not load pack sizes."); }

  const products = productsRes.value ?? [];
  const skus = products
    .filter((p) => p.active !== false)
    .map((p) => p.name)
    .filter(Boolean);

  // Relabel every line through its FK, so lines whose SKU was renamed still
  // land on the right grid row instead of disappearing.
  const byId = new Map(products.filter((p) => p.id && p.name).map((p) => [p.id, p.name]));
  const items = (itemsRes.value ?? []).map((it) =>
    it.product_id && byId.has(it.product_id) && byId.get(it.product_id) !== it.sku_name
      ? { ...it, sku_name: byId.get(it.product_id) }
      : it
  );

  // The account's own bottle sizes, in their own order — drives the per-size
  // total rows at the bottom of the grid.
  const sizes = [...new Set((sizesRes.value ?? []).map((r) => r.name).filter(Boolean))];
  // …and its pack sizes, likewise in the account's own order. The grid groups
  // SKU rows by "Size / Pack", so without this the packs sort alphabetically by
  // FLAVOR and every group header repeats (backlog 293).
  const packs = [...new Set((packsRes.value ?? []).map((r) => r.name).filter(Boolean))];

  return { orders: ordersRes.value ?? [], items, skus, sizes, packs };
}

// Columns a client is allowed to set on an order. DB-controlled columns
// (id, account_id, sort_order, timestamps, po_file_*) are never accepted here.
const ORDER_WRITABLE = new Set([
  "status", "state", "distributor", "warehouse", "copacker_po",
  "distributor_po", "target_ship_month", "po_receipt_date", "pickup_date",
  "notes", "visible",
]);

/** Update a single whitelisted field on an order. */
export async function updateOrderField(id, field, value) {
  if (!ORDER_WRITABLE.has(field)) throw new Error("That field can't be edited.");
  const { error } = await supabase.from("orders").update({ [field]: value ?? null }).eq("id", id);
  if (error) {
    console.error("[ordersService.updateOrderField]", error);
    throw new Error("Could not save the order.");
  }
  return { id };
}

/** Create a new blank order, appended to the end, with an auto CoPacker PO #
 *  when auto-PO is enabled. Returns the inserted row. */
export async function createOrderFlow() {
  const acct = await activeAccountId();

  // Next sort_order = max + 1 (append to the right of the grid).
  const { data: maxRow } = await supabase
    .from("orders").select("sort_order").eq("account_id", acct)
    .order("sort_order", { ascending: false }).limit(1).maybeSingle();
  const nextSort = (maxRow?.sort_order ?? -1) + 1;

  // Auto CoPacker PO # (atomic RPC; returns null when disabled).
  let copackerPo = null;
  const { data: po, error: poErr } = await supabase.rpc("assign_next_copacker_po", { p_account_id: acct });
  if (poErr) console.error("[ordersService.createOrderFlow:assign_next_copacker_po]", poErr);
  else copackerPo = po ?? null;

  const { data, error } = await supabase
    .from("orders")
    .insert({
      account_id: acct,
      status: "Order Received",
      visible: true,
      sort_order: nextSort,
      copacker_po: copackerPo,
    })
    .select("*")
    .single();
  if (error) {
    console.error("[ordersService.createOrderFlow]", error);
    throw new Error("Could not create the order.");
  }
  return data;
}

/** Delete an order (its items cascade in the DB). */
export async function deleteOrderFlow(id) {
  const { error } = await supabase.from("orders").delete().eq("id", id);
  if (error) {
    console.error("[ordersService.deleteOrderFlow]", error);
    throw new Error("Could not delete the order.");
  }
  return { id };
}

/** Upsert one line-item quantity for (order, sku). */
export async function upsertOrderItem(orderId, skuName, quantity, sortOrder) {
  const acct = await activeAccountId();
  const qty = parseInt(quantity, 10) || 0;
  // Stamp the SKU's id alongside the label so the line survives a rename.
  const { byName } = await productIndex();
  const { error } = await supabase
    .from("order_items")
    .upsert(
      {
        account_id: acct,
        order_id: orderId,
        sku_name: skuName,
        product_id: byName.get(skuName) ?? null,
        quantity: qty,
        sort_order: sortOrder ?? 0,
      },
      { onConflict: "order_id,sku_name" }
    );
  if (error) {
    console.error("[ordersService.upsertOrderItem]", error);
    throw new Error("Could not save the quantity.");
  }
  return { orderId, skuName, quantity: qty };
}

// =====================================================================
// PO FILE ATTACHMENT  (private distributor-order-pos bucket)
// Binary write goes straight to storage (path-prefix/owner-scoped policy in
// 0049); the metadata persists onto the order row via RLS.
// =====================================================================

export async function uploadOrderPOFile({ orderId, file, distributorPo }) {
  if (!orderId || !file) throw new Error("orderId and file are required");
  const email = await callerEmail();

  const cleanName = String(file.name || "po").replace(/[^A-Za-z0-9._-]+/g, "_");
  const filePath = `${orderId}/${Date.now()}-${cleanName}`;

  const { error: upErr } = await supabase.storage
    .from(PO_BUCKET)
    .upload(filePath, file, { contentType: file.type || "application/octet-stream", upsert: false });
  if (upErr) {
    console.error("[ordersService.uploadOrderPOFile:upload]", upErr);
    throw new Error("Could not upload the file.");
  }

  const patch = {
    po_file_path: filePath,
    po_file_name: file.name || cleanName,
    po_file_size: file.size ?? null,
    po_file_content_type: file.type || null,
    po_file_uploaded_at: new Date().toISOString(),
    po_file_uploaded_by: email,
  };
  if (distributorPo !== undefined) patch.distributor_po = distributorPo === "" ? null : distributorPo;

  const { error } = await supabase.from("orders").update(patch).eq("id", orderId);
  if (error) {
    console.error("[ordersService.uploadOrderPOFile:meta]", error);
    throw new Error("Uploaded the file but could not save its details.");
  }
  return patch;
}

/** Returns { url, file } — a 1h signed URL for previewing the attached PO. */
export async function getOrderPOFileUrl(orderId) {
  const { data: order, error: ordErr } = await supabase
    .from("orders")
    .select("po_file_path, po_file_name, po_file_content_type")
    .eq("id", orderId).single();
  if (ordErr) {
    console.error("[ordersService.getOrderPOFileUrl:order]", ordErr);
    throw new Error("Could not load the order.");
  }
  if (!order?.po_file_path) return { url: null, file: null };

  const { data, error } = await supabase.storage
    .from(PO_BUCKET).createSignedUrl(order.po_file_path, 3600);
  if (error) {
    console.error("[ordersService.getOrderPOFileUrl:sign]", error);
    throw new Error("Could not load the attachment.");
  }
  return { url: data?.signedUrl ?? null, file: order };
}

/** Remove the attached PO from storage + clear the metadata columns. */
export async function clearOrderPOFile(orderId) {
  const { data: order, error: ordErr } = await supabase
    .from("orders").select("po_file_path").eq("id", orderId).single();
  if (ordErr) {
    console.error("[ordersService.clearOrderPOFile:order]", ordErr);
    throw new Error("Could not load the order.");
  }
  if (order?.po_file_path) {
    const { error: rmErr } = await supabase.storage.from(PO_BUCKET).remove([order.po_file_path]);
    if (rmErr) console.error("[ordersService.clearOrderPOFile:remove]", rmErr); // best-effort
  }
  const { error } = await supabase.from("orders").update({
    po_file_path: null, po_file_name: null, po_file_size: null,
    po_file_content_type: null, po_file_uploaded_at: null, po_file_uploaded_by: null,
  }).eq("id", orderId);
  if (error) {
    console.error("[ordersService.clearOrderPOFile:meta]", error);
    throw new Error("Could not remove the attachment.");
  }
  return { id: orderId };
}

/** Save just the distributor_po (used when the modal edits the PO# only). */
export async function saveOrderDistributorPo(orderId, distributorPo) {
  return updateOrderField(orderId, "distributor_po", distributorPo === "" ? null : distributorPo);
}

// =====================================================================
// AUTO COPACKER PO # SETTINGS  (forecast_settings key 'auto_po_settings')
// =====================================================================

export async function loadAutoPOSettings() {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("forecast_settings").select("value")
    .eq("account_id", acct).eq("key", "auto_po_settings").maybeSingle();
  if (error) {
    console.error("[ordersService.loadAutoPOSettings]", error);
    throw new Error("Could not load PO settings.");
  }
  const v = data?.value ?? {};
  return { enabled: !!v.enabled, starting: Number(v.starting) || 1, last_assigned: Number(v.last_assigned) || 0 };
}

export async function saveAutoPOSettings({ enabled, starting }) {
  const acct = await activeAccountId();
  const startNum = parseInt(starting, 10);
  if (!Number.isFinite(startNum) || startNum < 0) throw new Error("Starting PO # must be a non-negative whole number.");

  // Preserve the server-managed last_assigned.
  const current = await loadAutoPOSettings();
  const { error } = await supabase
    .from("forecast_settings")
    .upsert(
      { account_id: acct, key: "auto_po_settings",
        value: { enabled: !!enabled, starting: startNum, last_assigned: current.last_assigned } },
      { onConflict: "account_id,key" }
    );
  if (error) {
    console.error("[ordersService.saveAutoPOSettings]", error);
    throw new Error("Could not save PO settings.");
  }
  return { enabled: !!enabled, starting: startNum, last_assigned: current.last_assigned };
}

// =====================================================================
// SUMMARY TAB  (shipments by pickup_date + editable sales goals)
// =====================================================================

/**
 * Order-item rows for orders SHIPPED in [startDate, endDate].
 * Each row carries its parent order's { state, pickup_date } so the client can
 * bucket by actual shipment month. Shape: [{ sku_name, quantity, order:{...} }].
 *
 * ⚠️ Two filters here are load-bearing, and both were missing (backlog 238):
 *  - `status = 'Shipped'`. This feeds the column headed **Actual**, next to a
 *    Goal. A booked `pickup_date` is a PLAN, not a shipment: an order sitting
 *    at "Pickup Requested" with a date on it used to count as delivered
 *    performance. "Shipped" is the terminal status in STATUS_OPTIONS, so there
 *    is no later state for this to undercount.
 *  - `visible != false`. The comparison tab has always applied it
 *    (loadOrderRowsForMonths); this one did not, so hiding an order changed one
 *    tab's totals and not the other's, with nothing on screen to explain why.
 */
export async function loadShipmentRowsForRange(startDate, endDate) {
  const acct = await activeAccountId();
  let data;
  try {
    data = await fetchAllRows(() => supabase
      .from("orders")
      .select("id, state, pickup_date, items:order_items(sku_name, quantity, product_id)")
      .eq("account_id", acct)
      .eq("status", "Shipped")
      .neq("visible", false)
      .not("pickup_date", "is", null)
      .gte("pickup_date", startDate)
      .lte("pickup_date", endDate)
      .order("id", { ascending: true }), { label: "shipments" });
  } catch (error) {
    console.error("[ordersService.loadShipmentRowsForRange]", error);
    throw new Error("Could not load shipments.");
  }
  const { byId } = await productIndex();
  const rows = [];
  for (const o of data ?? []) {
    const order = { id: o.id, state: o.state, pickup_date: o.pickup_date };
    for (const it of o.items ?? []) {
      rows.push({ sku_name: resolveSkuName(it, byId), quantity: it.quantity, order });
    }
  }
  return rows;
}

/** Sales-goal rows whose (year, month) falls in [startDate, endDate]. */
export async function loadSalesGoalsForRange(startDate, endDate) {
  const acct = await activeAccountId();
  const startY = Number(startDate.slice(0, 4));
  const endY = Number(endDate.slice(0, 4));
  let data;
  try {
    data = await fetchAllRows(() => supabase
      .from("sales_goals").select("region, bottle_size, year, month, goal_cases")
      .eq("account_id", acct)
      .gte("year", startY)
      .lte("year", endY)
      .order("id", { ascending: true }), { label: "sales_goals" });
  } catch (error) {
    console.error("[ordersService.loadSalesGoalsForRange]", error);
    throw new Error("Could not load goals.");
  }
  // Trim partial-year edges to the exact month window.
  const startKey = startDate.slice(0, 7); // YYYY-MM
  const endKey = endDate.slice(0, 7);
  return (data ?? []).filter((r) => {
    const k = `${r.year}-${String(r.month).padStart(2, "0")}`;
    return k >= startKey && k <= endKey;
  });
}

/** Upsert one goal cell. goalCases null/0 clears the target. */
export async function upsertSalesGoal({ region, bottleSize, year, month, goalCases }) {
  const acct = await activeAccountId();
  const email = await callerEmail();
  const value = Number(goalCases) || 0;
  const { error } = await supabase
    .from("sales_goals")
    .upsert(
      { account_id: acct, region, bottle_size: bottleSize, year: Number(year),
        month: Number(month), goal_cases: value, updated_by: email },
      { onConflict: "account_id,region,bottle_size,year,month" }
    );
  if (error) {
    console.error("[ordersService.upsertSalesGoal]", error);
    throw new Error("Could not save the goal.");
  }
  return { region, bottleSize, year, month, goalCases: value };
}

// =====================================================================
// FORECAST-VS-ORDERS COMPARISON
// =====================================================================

/**
 * Order-item rows for orders targeted at the given months.
 *
 * `months` is the comparison tab's window: [{ month: "January", year: 2026 }, …].
 * ⚠️ The YEAR is load-bearing. This used to take bare month NAMES and filter
 * `.in("target_ship_month", ["January", …])`, which matched every January the
 * tenant had ever ordered for — so a window spanning a year boundary
 * double-counted, and the tab's own year picker changed nothing (backlog 237).
 * `target_ship_month` is a real date as of migration 0085, so the filter is now
 * a range over month-starts, served by orders_account_target_month_idx.
 *
 * Shape: [{ sku_name, quantity, order:{ state, distributor, target_ship_month } }]
 * where target_ship_month is the stored "YYYY-MM-01".
 */
export async function loadOrderRowsForMonths(months) {
  const acct = await activeAccountId();
  const starts = (months ?? [])
    .map((m) => monthStartFromNameYear(m.month, m.year))
    .filter(Boolean);
  const range = monthRange(starts);
  const build = () => {
    let q = supabase
      .from("orders")
      .select("state, distributor, target_ship_month, items:order_items(sku_name, quantity, product_id)")
      .eq("account_id", acct)
      .neq("visible", false);
    // A contiguous window is the normal case, but the range is only a coarse
    // prefilter — the caller keys on the exact month, so a non-contiguous
    // selection still lands on the right cells.
    if (range) q = q.gte("target_ship_month", range.from).lte("target_ship_month", range.to);
    return q.order("id", { ascending: true });
  };
  let data;
  try {
    data = await fetchAllRows(build, { label: "orders (by month)" });
  } catch (error) {
    console.error("[ordersService.loadOrderRowsForMonths]", error);
    throw new Error("Could not load orders.");
  }
  const { byId } = await productIndex();
  const rows = [];
  for (const o of data ?? []) {
    const order = { state: o.state, distributor: o.distributor, target_ship_month: o.target_ship_month };
    for (const it of o.items ?? []) {
      rows.push({ sku_name: resolveSkuName(it, byId), quantity: it.quantity, order });
    }
  }
  return rows;
}

/**
 * CROSS-MODULE READ (sales-forecasting's `forecasts`): forecast case rows for
 * the requested (month, year) pairs. Shape mirrors what the comparison expects:
 *   [{ month, year, case_forecast, states:{ state_name, region, distributor },
 *      products:{ name } }]
 * See ARCHITECTURE.md "Cross-module dependencies".
 */
export async function loadForecastRowsForMonths(monthYears) {
  const acct = await activeAccountId();
  const years = [...new Set((monthYears ?? []).map((m) => Number(m.year)).filter(Number.isFinite))];
  const build = () => {
    let q = supabase
      .from("forecasts")
      .select("month, year, case_forecast, states:sales_states(state_name, region, distributor), products:products(name)")
      .eq("account_id", acct);
    if (years.length) q = q.in("year", years);
    return q.order("id", { ascending: true });
  };
  let data;
  try {
    data = await fetchAllRows(build, { label: "forecasts (cross-module)" });
  } catch (error) {
    console.error("[ordersService.loadForecastRowsForMonths]", error);
    throw new Error("Could not load forecasts.");
  }
  // Keep only the requested (month, year) pairs.
  const wanted = new Set((monthYears ?? []).map((m) => `${m.month}|${m.year}`));
  return (data ?? []).filter((r) => wanted.has(`${r.month}|${r.year}`));
}

// =====================================================================
// OPERATIONS-HUB (Home) SUMMARY
// =====================================================================

/** How far back a "Needs you" signal looks. See the note in the JSDoc below. */
const SIGNAL_WINDOW_MONTHS = 12;

/** Local-calendar YYYY-MM-DD. Never `toISOString()` — that is UTC, and would
 *  move the boundary by a day for every US user (see monthDate.js). */
const ymdLocal = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/**
 * Two lean passes over the account's visible orders for the Home dashboard.
 * Returns { shippedMtd, shippedYtd, onHold, stalePickup, noTargetMonth }.
 *
 * ⚠️ This was ONE query — every order the account had ever placed, with every
 * line item embedded, summed in JS with no date bound at all (backlog 465).
 * Home runs every module's provider on every load, so a tenant three years in
 * paid for three years of history to render one tile. Split in two so the
 * expensive half is the bounded half:
 *
 *  1. **Shipped totals** carry the `order_items` embed, and both numbers they
 *     feed are year-to-date or narrower — so this fetch is bounded to the
 *     current calendar year and served by `orders_account_pickup_idx`. Same
 *     filter set as `loadShipmentRowsForRange`: a booked `pickup_date` is a
 *     PLAN, not a shipment, so `status = 'Shipped'` is load-bearing (238).
 *  2. **The three signal counts** carry three narrow columns and no `order_items`
 *     embed — a count never needed a quantity — and are bounded to orders with
 *     activity inside `SIGNAL_WINDOW_MONTHS`.
 *
 * ⚠️ THE SIGNAL WINDOW IS NOT A CALENDAR YEAR, AND THAT IS THE POINT.
 * "Shipped year-to-date" is a calendar question, so its bound is Jan 1–Dec 31.
 * "Does this order need a human?" is not: on 2 January a calendar bound would
 * drop every order placed in December, which is precisely the work most likely
 * to still need someone. So this half uses a ROLLING window instead — and it
 * hangs off `updated_at`, not `created_at`, because the question is whether the
 * order is still live work. An order opened 14 months ago that someone edited
 * yesterday is live; an order nobody has touched since 2024 is not, and no
 * amount of surfacing it on Home has ever made anyone act on it.
 *
 * This half used to be deliberately unbounded, on the argument that an order On
 * Hold since 2024 is "still true today". It is — and it is also a count that can
 * only ever grow, on a panel headed *Needs you*: a signal that cannot be cleared
 * by doing the work is not a signal, it is furniture, and it costs a full-table
 * read on every single Home load to render.
 *
 * `visible` is filtered here for the same reason it is in the other two tabs —
 * a hidden order should not be able to move a number the user sees (backlog 238).
 */
export async function loadDashboardOrderStats() {
  const acct = await activeAccountId();

  const now = new Date();
  const yNow = String(now.getFullYear());
  const ymNow = `${yNow}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const today = `${ymNow}-${String(now.getDate()).padStart(2, "0")}`;
  // Built through a Date so the arithmetic normalises: 29 Feb minus 12 months
  // is not a date, and the naive string form ("2027-02-29") is a 400 from
  // PostgREST — i.e. Home loses this module's signals entirely, one day in four
  // years, for a reason nobody would find.
  const signalsSince = ymdLocal(
    new Date(now.getFullYear(), now.getMonth() - SIGNAL_WINDOW_MONTHS, now.getDate()),
  );

  let shippedRows, signalRows;
  try {
    [shippedRows, signalRows] = await Promise.all([
      fetchAllRows(() => supabase
        .from("orders")
        .select("pickup_date, items:order_items(quantity)")
        .eq("account_id", acct)
        .eq("status", "Shipped")
        .neq("visible", false)
        .not("pickup_date", "is", null)
        .gte("pickup_date", `${yNow}-01-01`)
        .lte("pickup_date", `${yNow}-12-31`)
        .order("id", { ascending: true }), { label: "dashboard shipments" }),
      fetchAllRows(() => supabase
        .from("orders")
        .select("status, target_ship_month, pickup_date")
        .eq("account_id", acct)
        .neq("visible", false)
        .gte("updated_at", signalsSince)
        .order("id", { ascending: true }), { label: "dashboard order signals" }),
    ]);
  } catch (error) {
    console.error("[ordersService.loadDashboardOrderStats]", error);
    throw new Error("Could not load order stats.");
  }

  const out = { shippedMtd: 0, shippedYtd: 0, onHold: 0, stalePickup: 0, noTargetMonth: 0 };

  for (const o of shippedRows ?? []) {
    const qty = (o.items ?? []).reduce((s, it) => s + (Number(it.quantity) || 0), 0);
    // The fetch IS the year test now — every row here is Shipped with a
    // pickup_date inside the current calendar year.
    out.shippedYtd += qty;
    if (String(o.pickup_date).slice(0, 7) === ymNow) out.shippedMtd += qty;
  }

  // Every row here has been touched within SIGNAL_WINDOW_MONTHS — all three
  // counts below are "…and someone has worked this order in the last year",
  // not "…ever".
  for (const o of signalRows ?? []) {
    const pickup = o.pickup_date ? String(o.pickup_date) : null;
    if (o.status === "On Hold / Review Needed") out.onHold += 1;
    // A booked pickup date that has come and gone while the order is still not
    // Shipped: either it shipped and nobody said so, or it slipped. Both need a
    // human. This is the same population that used to be counted as delivered
    // performance on the Summary tab before 238.
    if (o.status !== "Shipped" && pickup && pickup < today) out.stalePickup += 1;
    if (!o.target_ship_month) out.noTargetMonth += 1;
  }
  return out;
}

// =====================================================================
// FORECASTING ENTITLEMENT  (Forecast Comparison tab gate)
// =====================================================================

/**
 * Does this tenant's plan include Sales Forecasting?
 *
 * The Forecast Comparison tab reads `forecasts`, and the ONLY writer of that
 * table is the sales-forecasting module — which is a Scale-plan feature, while
 * this module ships from Growth up. Without this check a Growth customer opens
 * the tab to a Forecast column of zeros and a Variance column that is just the
 * negative of Ordered, with nothing on screen saying why (backlog 236/197).
 * RLS returns the same empty set for "not entitled" and for "entitled but
 * nothing forecast yet", so the tab cannot tell those apart on its own.
 *
 * Calls the same `account_has_module` the RLS policies use, so the UI cannot
 * drift from the server's answer. That function is SECURITY DEFINER but pins on
 * `m.user_id = auth.uid()` internally, so it can only ever answer for the
 * caller's own memberships — this is a presentation gate, not the boundary.
 * The boundary is RLS on `forecasts`, which is unchanged.
 *
 * Fails CLOSED-ish: on error it returns `true`, i.e. show the grid. A broken
 * entitlement read should never hide a tab from a customer who paid for it —
 * and if they did not pay for it, the worst case is the zeros they see today.
 */
export async function hasForecastingEntitlement() {
  try {
    const acct = await activeAccountId();
    const { data, error } = await supabase.rpc("account_has_module", {
      acct,
      mod: "sales_forecasting",
    });
    if (error) throw error;
    return data !== false;
  } catch (error) {
    console.error("[ordersService.hasForecastingEntitlement]", error);
    return true;
  }
}
